import { RestClient } from '@libs/rest-client';
import type { ReactNode } from 'react';
import { createContext, useContext, useEffect, useState } from 'react';

interface ReleaseFlagContextValue {
  releaseFlags: Record<string, unknown>;
  isLoading: boolean;
}

const defaultContextValue: ReleaseFlagContextValue = {
  releaseFlags: {},
  isLoading: false,
};

const ReleaseFlagContext = createContext<ReleaseFlagContextValue>(defaultContextValue);

export function ReleaseFlagProvider({ children }: { children: ReactNode }) {
  const [releaseFlags, setReleaseFlags] = useState<Record<string, unknown>>({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function fetchFlags() {
      try {
        const response = await RestClient.auth.get<{
          releaseFlags: Record<string, unknown>;
        }>('/me/release-flags');

        if (!cancelled) {
          setReleaseFlags(response.releaseFlags ?? {});
        }
      } catch {
        if (!cancelled) {
          setReleaseFlags({});
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    fetchFlags();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <ReleaseFlagContext.Provider value={{ releaseFlags, isLoading }}>{children}</ReleaseFlagContext.Provider>
  );
}

export function useReleaseFlag(flagKey: string): { isEnabled: boolean; isLoading: boolean } {
  const { releaseFlags, isLoading } = useContext(ReleaseFlagContext);
  const isEnabled = releaseFlags[flagKey] === true;
  return { isEnabled, isLoading };
}
